import { createEnhance } from '@halo/zustand-enhance';
import { useAppStore } from '@halo/chat-sdk-react';
import { promiseTimeout, safeJsonParse } from '@halo/utils';
import { toaster } from '@halo/ui-react';
import { useWalletStore } from '@/wallet';
import { userApis } from '@/configs/apis';
import { formatAccountsData } from '@/scripts/cloud/helper';
import { useSessionStore } from '../session';
import { useContactsStore } from '../contacts';
import { ChatLinesEnum, SyncBackupStatus, SyncBackupType, type SettingsStore } from './types';

export const useSettingsStore = createEnhance<SettingsStore>(
  (set) => ({
    editDelayedTime: 60 * 60 * 1000, // 1 hour
    setEditDelayedTime: (time) => set({ editDelayedTime: time }),
    luckyBoxDeadline: 24 * 60 * 60 * 1000, // 15 minutes
    setLuckyBoxDeadline: (deadline) => set({ luckyBoxDeadline: deadline }),
    normal: 14,
    marks: [12, 14, 16, 18, 20, 22, 24],
    fontSize: 14,
    setFontSize: (size) => set({ fontSize: size }),
    chatLines: ChatLinesEnum.Three,
    setChatLines: (lines) => set({ chatLines: lines }),
    syncBackupContactChecked: true,
    syncBackupWalletChecked: true,
    setSyncAndBackupContacts: (value) => set({ syncBackupContactChecked: value }),
    setSyncAndBackupWallet: (value) => set({ syncBackupWalletChecked: value }),
    syncBackupStatus: null,
    syncBackupCloudData: async (params) => {
      const { type = SyncBackupType.ALL, info = {} } = params || {};
      const {
        deleted_accounts = [],
        deleted_wallets = [],
        accounts: localNewAccounts,
        updateTime: localNewUpdateTime,
        isChangedPassword: localNewIsChangedPassword,
      } = info;
      try {
        const { user, deviceId, client } = useAppStore.getState();
        const { walletCloud } = useSessionStore.getState();
        const { syncBackupContactChecked, syncBackupWalletChecked } = useSettingsStore.getState();
        if (!walletCloud) {
          return;
        }
        set({ syncBackupStatus: null });
        const {
          accounts: localAccounts,
          updateTime: localUpdateTime,
          isChangedPassword: localIsChangedPassword,
          readonly,
        } = useWalletStore.getState();

        const accounts = formatAccountsData(localNewAccounts || localAccounts);
        const updateTime = localNewUpdateTime || localUpdateTime;
        const isChangedPassword = localNewIsChangedPassword ?? localIsChangedPassword;

        if ((syncBackupContactChecked || syncBackupWalletChecked) && !readonly) {
          const cloudData = await promiseTimeout(walletCloud.getCloudData, 5000);
          if (cloudData?.wallet?.accounts?.length) {
            const cloudHasChangeData = {
              isChange: false,
              edited_version: null,
            };
            const lcoalHasChangeData = {
              isChange: false,
            };
            let localData = null;
            if (
              syncBackupWalletChecked &&
              [SyncBackupType.ALL, SyncBackupType.WALLET].includes(type)
            ) {
              const waitMergeCloudData = {
                accounts: cloudData.wallet.accounts || [],
                t: cloudData.wallet.t,
                isChange: cloudData.wallet.isChange,
                edited_version: null,
              };
              const waitMergeLocalData = {
                accounts,
                t: updateTime,
                isChange: isChangedPassword,
                deleted_accounts,
                deleted_wallets,
              };

              const mergedData = await client?.Api.wallet
                .pullWalletFromCloudV2({
                  cloud_data:
                    cloudData.wallet.backupCloudData || JSON.stringify(waitMergeCloudData),
                  local_data: JSON.stringify(waitMergeLocalData),
                  local_device: user.user_id + '_' + deviceId,
                })
                .catch((err) => {
                  if (err.code === -10000) {
                    toaster.error({
                      title: 'The cloud drive is synchronizing. Please try again later',
                    });
                  }
                  throw err;
                });

              if (mergedData?.cloud_changed) {
                const backupData = safeJsonParse(mergedData.cloud_data, waitMergeCloudData);
                cloudHasChangeData.isChange = true;
                cloudHasChangeData.edited_version = backupData.edited_version;
                cloudData.wallet = {
                  ...cloudData.wallet,
                  accounts: backupData.accounts,
                  isChange: backupData.isChange,
                  t: backupData.t,
                  password: backupData?.isChange ? '' : cloudData.wallet.password,
                  backupCloudData: mergedData.cloud_data,
                };
              }

              if (mergedData?.local_changed) {
                localData = safeJsonParse(mergedData.local_data, waitMergeLocalData);
              } else {
                localData = waitMergeLocalData;
              }
            }
            if (
              syncBackupContactChecked &&
              [SyncBackupType.ALL, SyncBackupType.CONTACTS].includes(type)
            ) {
              const mergedContacts = await client?.Api.contacts
                .pullContactsFromCloud({
                  cloud_data: cloudData.contacts || '{}',
                  local_device: user.user_id + '_' + deviceId,
                })
                .catch((_) => null);

              if (mergedContacts?.changed) {
                lcoalHasChangeData.isChange = true;
                cloudData.contacts = mergedContacts.data;

                useContactsStore.getState().getContacts(client!);
              }
            }
            if (cloudHasChangeData.isChange || lcoalHasChangeData.isChange) {
              await walletCloud.updateCloudData(cloudData);
              if (cloudHasChangeData.isChange && cloudHasChangeData.edited_version) {
                client?.Api.wallet
                  .setCloudWalletVersion({
                    edited_version: cloudHasChangeData.edited_version,
                  })
                  .catch();
              }
            }

            if (localData) {
              await useWalletStore
                .init({
                  userId: userApis.getUserId(),
                  token: userApis.getToken(),
                  accounts: localData?.accounts,
                  defaultPassword: localData?.isChange ? '' : cloudData.wallet.password,
                  isChangedPassword: localData.isChange,
                  updateTime: localData.t,
                })
                .catch();
            }
          }
        }
      } catch (error: any) {
        const err: any = safeJsonParse(error.message, {});
        if (err?.status === 401) {
          set({ syncBackupStatus: SyncBackupStatus.UN_AUTHORIZED });
        } else {
          set({ syncBackupStatus: SyncBackupStatus.ABNORMAL });
        }
        throw error;
      }
    },
  }),
  {
    persist: {
      name: 'settings',
    },
  }
);
